import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  message,
  Popconfirm,
  Select,
  DatePicker,
  Input,
  Row,
  Col,
  Statistic,
  Modal
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SearchOutlined,
  PrinterOutlined,
  DownloadOutlined,
  SendOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import vendorBillService, { VendorBill, VendorBillFilters } from '../../../../services/vendorBillService';
import VendorBillForm from './VendorBillForm';
import apiService from '../../../../services/apiService';

const { Option } = Select;
const { RangePicker } = DatePicker;

const VendorBillList: React.FC = () => {
  const [bills, setBills] = useState<VendorBill[]>([]);
  const [loading, setLoading] = useState(false);
  const [formVisible, setFormVisible] = useState(false);
  const [selectedBill, setSelectedBill] = useState<VendorBill | null>(null);
  const [detailsVisible, setDetailsVisible] = useState(false);
  const [filters, setFilters] = useState<VendorBillFilters>({});
  const [stats, setStats] = useState({
    totalBills: 0,
    draftBills: 0,
    postedBills: 0,
    paidBills: 0,
    overdueBills: 0,
    totalAmountDue: 0,
    totalAmountOverdue: 0
  });

  useEffect(() => {
    loadBills();
    loadStats();
  }, []);

  const loadBills = async () => {
    try {
      setLoading(true);
      const data = await vendorBillService.getAllBills(filters);
      setBills(data);
    } catch (error: any) {
      console.error('Failed to load vendor bills:', error);
      message.error('Failed to load vendor bills');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const data = await vendorBillService.getStats();
      setStats(data);
    } catch (error: any) {
      console.error('Failed to load stats:', error);
    }
  };

  const handleCreate = () => {
    setSelectedBill(null);
    setFormVisible(true);
  };

  const handleEdit = (bill: VendorBill) => {
    setSelectedBill(bill);
    setFormVisible(true);
  };

  const handleView = (bill: VendorBill) => {
    setSelectedBill(bill);
    setDetailsVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await vendorBillService.deleteBill(id);
      message.success('Vendor bill deleted successfully');
      loadBills();
      loadStats();
    } catch (error: any) {
      message.error(`Failed to delete vendor bill: ${error.message}`);
    }
  };

  /**
   * Resolve the AP account, expense account and purchase journal a bill needs
   * for posting OR cancelling. Cancelling writes a reversing journal entry, so
   * it needs exactly the same references as posting.
   * Returns null (after showing the error) when anything is missing.
   */
  const resolveBillPostingRefs = async (bill: VendorBill, action: 'post' | 'cancel') => {
    try {
      // Fetch accounts, journals AND accounting settings — settings hold the
      // canonical Default AP / Expense account ids when configured, which
      // wins over code-based heuristics on databases where AP was migrated
      // away from the legacy "2100" code.
      const [accountsResponse, journalsResponse, settingsResponse] = await Promise.all([
        apiService.get<any>('/accounting/accounts'),
        apiService.get<any>('/accounting/journals/active'),
        apiService.get<any>('/accounting/settings').catch(() => null)
      ]);
      const accounts = accountsResponse.data || [];
      const journals = journalsResponse.data || [];
      const settings = settingsResponse?.data || null;

      const apById = settings?.defaultPayableAccountId
        ? accounts.find((a: any) => a.id === settings.defaultPayableAccountId)
        : null;
      const expenseById = settings?.defaultExpenseAccountId
        ? accounts.find((a: any) => a.id === settings.defaultExpenseAccountId)
        : null;
      const apAccount = apById
        || accounts.find((a: any) => a.code === '220000')
        || accounts.find((a: any) => a.code === '210000')
        || accounts.find((a: any) => a.code === '2100')
        || accounts.find((a: any) => a.code === '2000')
        || accounts.find((a: any) => a.code?.startsWith('2100'))
        || accounts.find((a: any) => a.code?.startsWith('22'))
        || accounts.find((a: any) =>
          a.name?.toLowerCase().includes('payable') ||
          a.name?.toLowerCase().includes('creditor')
        );
      // Safety-net only; backend uses each line item's accountId when set.
      const firstLineAccountId = bill.items?.find(it => it.accountId)?.accountId;
      const expenseAccount = expenseById
        || (firstLineAccountId ? accounts.find((a: any) => a.id === firstLineAccountId) : null)
        || accounts.find((a: any) => a.accountType === 'EXPENSE');
      const purchaseJournal = journals.find((j: any) => j.code === 'PUR')
        || journals.find((j: any) => j.code === 'BILL')
        || journals.find((j: any) => j.type === 'PURCHASE');

      if (!apAccount || !expenseAccount || !purchaseJournal) {
        console.error('Vendor Bill post — account/journal resolution failed:', {
          apAccount: apAccount ? { id: apAccount.id, code: apAccount.code } : null,
          expenseAccount: expenseAccount ? { id: expenseAccount.id, code: expenseAccount.code } : null,
          purchaseJournal: purchaseJournal ? { id: purchaseJournal.id, code: purchaseJournal.code, type: purchaseJournal.type } : null,
          defaultPayableAccountId: settings?.defaultPayableAccountId,
          defaultExpenseAccountId: settings?.defaultExpenseAccountId,
          allAccountCodes: accounts.map((a: any) => a.code),
          allJournalCodes: journals.map((j: any) => `${j.code}(${j.type})`)
        });
        const missing: string[] = [];
        if (!apAccount) missing.push('Accounts Payable');
        if (!expenseAccount) missing.push('Expense account');
        if (!purchaseJournal) missing.push('Purchase Journal');
        message.error(
          `Cannot ${action}: missing ${missing.join(', ')}. ` +
          `Set defaults under Accounting → Settings, or create accounts (AP code 2100/2000) and a journal with code PUR / type PURCHASE.`
        );
        return null;
      }

      const postData = {
        accountsPayableAccountId: apAccount.id,
        expenseAccountId: expenseAccount.id,
        journalId: purchaseJournal.id
      };

      return postData;
    } catch (error: any) {
      message.error(`Failed to prepare ${action}: ${error.message}`);
      return null;
    }
  };

  const handlePost = async (bill: VendorBill) => {
    const postData = await resolveBillPostingRefs(bill, 'post');
    if (!postData) return;
    try {
      await vendorBillService.postBill(bill.id, postData);
      message.success('Vendor bill posted successfully');
      loadBills();
      loadStats();
    } catch (error: any) {
      message.error(`Failed to post vendor bill: ${error.message}`);
    }
  };

  const handleCancel = async (bill: VendorBill) => {
    const postData = await resolveBillPostingRefs(bill, 'cancel');
    if (!postData) return;
    try {
      await vendorBillService.cancelBill(bill.id, postData);
      message.success('Vendor bill cancelled');
      loadBills();
      loadStats();
    } catch (error: any) {
      message.error(`Failed to cancel vendor bill: ${error.message}`);
    }
  };

  const handleFormSuccess = () => {
    loadBills();
    loadStats();
  };

  const handlePrint = async (bill: VendorBill) => {
    try {
      // Get the auth token
      const token = localStorage.getItem('authToken');
      if (!token) {
        message.error('No authentication token found. Please log in again.');
        return;
      }

      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api/v1';

      // Fetch HTML with authentication
      const response = await fetch(`${apiBaseUrl}/pdf/vendor-bill/${bill.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Print vendor bill failed:', response.status, errorText);
        throw new Error(`Failed to load vendor bill for printing (${response.status})`);
      }

      // Get HTML content and open in new window
      const html = await response.text();
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(html);
        printWindow.document.close();
        // Trigger print dialog after content loads
        printWindow.onload = () => {
          printWindow.print();
        };
      } else {
        message.error('Failed to open print window. Please allow popups for this site.');
      }
    } catch (error: any) {
      message.error(`Failed to print vendor bill: ${error.message}`);
    }
  };

  const handleDownloadPDF = async (bill: VendorBill) => {
    try {
      // Get the auth token
      const token = localStorage.getItem('authToken');
      if (!token) {
        message.error('No authentication token found. Please log in again.');
        return;
      }

      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api/v1';

      message.loading({ content: 'Generating PDF...', key: 'pdf-download' });

      // Fetch PDF with authentication
      const response = await fetch(`${apiBaseUrl}/pdf/vendor-bill/${bill.id}/download`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('PDF download failed:', response.status, errorText);
        throw new Error(`Failed to download PDF (${response.status})`);
      }

      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${bill.billNo}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      message.success({ content: 'PDF downloaded successfully', key: 'pdf-download' });
    } catch (error: any) {
      message.error({ content: `Failed to download PDF: ${error.message}`, key: 'pdf-download' });
    }
  };

  const handleSearch = () => {
    loadBills();
  };

  const handleResetFilters = () => {
    setFilters({});
    setTimeout(() => loadBills(), 0);
  };

  const handleExport = async () => {
    if (bills.length === 0) {
      message.warning('No data to export');
      return;
    }
    const hide = message.loading('Fetching vendor bill details for export...', 0);
    try {
      // Fetch full details (with line items) for each bill
      const fullBills = await Promise.all(
        bills.map(b => vendorBillService.getBillById(b.id).catch(() => b))
      );

      const wb = XLSX.utils.book_new();

      // Sheet 1: Bill Summary
      const summaryRows = fullBills.map(b => ({
        'Bill No': b.billNo || '',
        'Vendor': b.vendor?.name || '',
        'Date': b.billDate ? dayjs(b.billDate).format('YYYY-MM-DD') : '',
        'Due Date': b.dueDate ? dayjs(b.dueDate).format('YYYY-MM-DD') : '',
        'Total': Number(b.amountTotal || 0),
        'Amount Due': Number(b.amountResidual || 0),
        'Status': (b.state || '').toUpperCase(),
        'Payment Status': (b.paymentState || '').replace(/_/g, ' ').toUpperCase(),
      }));
      const ws1 = XLSX.utils.json_to_sheet(summaryRows);
      ws1['!cols'] = [
        { wch: 14 }, { wch: 30 }, { wch: 12 }, { wch: 12 },
        { wch: 14 }, { wch: 14 }, { wch: 12 }, { wch: 16 },
      ];
      XLSX.utils.book_append_sheet(wb, ws1, 'Bill Summary');

      // Sheet 2: Line Items
      const lineRows: Record<string, any>[] = [];
      fullBills.forEach(b => {
        if (b.items && b.items.length > 0) {
          b.items.forEach((item: any) => {
            lineRows.push({
              'Bill No': b.billNo || '',
              'Vendor': b.vendor?.name || '',
              'Description': item.description || '',
              'Quantity': Number(item.quantity || 0),
              'Unit Price': Number(item.unitPrice || 0),
              'Discount': Number(item.discount || 0),
              'Subtotal': Number(item.subtotal || 0),
            });
          });
        }
      });
      const ws2 = XLSX.utils.json_to_sheet(lineRows.length > 0 ? lineRows : [{ 'Note': 'No line items found' }]);
      ws2['!cols'] = [
        { wch: 14 }, { wch: 30 }, { wch: 36 }, { wch: 10 },
        { wch: 12 }, { wch: 12 }, { wch: 14 },
      ];
      XLSX.utils.book_append_sheet(wb, ws2, 'Line Items');

      XLSX.writeFile(wb, `Vendor_Bills_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`);
      hide();
      message.success(`Exported ${fullBills.length} vendor bills with line items`);
    } catch (error: any) {
      hide();
      message.error(`Export failed: ${error.message}`);
    }
  };

  const getStateColor = (state: string) => {
    switch (state) {
      case 'DRAFT':
        return 'default';
      case 'POSTED':
        return 'processing';
      case 'PAID':
        return 'success';
      case 'CANCELLED':
        return 'error';
      default:
        return 'default';
    }
  };

  const getPaymentStateColor = (paymentState: string) => {
    switch (paymentState) {
      case 'NOT_PAID':
        return 'error';
      case 'PARTIAL':
        return 'warning';
      case 'PAID':
        return 'success';
      default:
        return 'default';
    }
  };

  const columns: ColumnsType<VendorBill> = [
    {
      title: 'Bill No',
      dataIndex: 'billNo',
      key: 'billNo',
      width: 150,
      fixed: 'left',
      render: (billNo) => <strong>{billNo}</strong>
    },
    {
      title: 'Vendor',
      dataIndex: 'vendor',
      key: 'vendor',
      width: 150,
      render: (vendor) => vendor?.name || '-'
    },
    {
      title: 'Bill Date',
      dataIndex: 'billDate',
      key: 'billDate',
      width: 110,
      render: (date) => dayjs(date).format('YYYY-MM-DD')
    },
    {
      title: 'Due Date',
      dataIndex: 'dueDate',
      key: 'dueDate',
      width: 110,
      render: (date) => date ? dayjs(date).format('YYYY-MM-DD') : '-'
    },
    {
      title: 'Amount',
      dataIndex: 'amountTotal',
      key: 'amountTotal',
      width: 120,
      align: 'right',
      render: (amount) => `$${Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    },
    {
      title: 'Amount Due',
      dataIndex: 'amountResidual',
      key: 'amountResidual',
      width: 120,
      align: 'right',
      render: (amount) => (
        <span style={{ color: Number(amount) > 0 ? '#ff4d4f' : '#52c41a', fontWeight: 500 }}>
          ${Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </span>
      )
    },
    {
      title: 'State',
      dataIndex: 'state',
      key: 'state',
      width: 100,
      render: (state) => <Tag color={getStateColor(state)}>{state}</Tag>
    },
    {
      title: 'Payment',
      dataIndex: 'paymentState',
      key: 'paymentState',
      width: 100,
      render: (paymentState) => (
        <Tag color={getPaymentStateColor(paymentState)}>
          {paymentState.replace('_', ' ')}
        </Tag>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      // Wide enough for the full row of buttons incl. Cancel; at 340 the last
      // button (PDF) was clipped mid-word.
      width: 420,
      fixed: 'right',
      render: (_, record) => (
        <Space size={4}>
          <Button
            type="default"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleView(record)}
          >
            View
          </Button>
          {record.state !== 'CANCELLED' && (
            <Button
              type="default"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            >
              Edit
            </Button>
          )}
          {record.state === 'DRAFT' && (
            <Popconfirm
              title="Post this vendor bill?"
              description="Once posted, a journal entry will be created."
              onConfirm={() => handlePost(record)}
              okText="Post"
              cancelText="Cancel"
            >
              <Button
                type="primary"
                size="small"
                icon={<SendOutlined />}
              >
                Post
              </Button>
            </Popconfirm>
          )}
          {record.state !== 'CANCELLED' && record.state !== 'PAID' && (
            <Popconfirm
              title="Cancel this vendor bill?"
              description="A reversing journal entry will be created. This cannot be undone."
              onConfirm={() => handleCancel(record)}
              okText="Yes, cancel it"
              cancelText="No"
            >
              <Button
                type="default"
                size="small"
                danger
                icon={<CloseCircleOutlined />}
              >
                Cancel
              </Button>
            </Popconfirm>
          )}
          {(record.state === 'DRAFT' || record.state === 'CANCELLED') && (
            <Popconfirm
              title="Are you sure you want to delete this vendor bill?"
              onConfirm={() => handleDelete(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <Button
                type="default"
                size="small"
                danger
                icon={<DeleteOutlined />}
              >
                Delete
              </Button>
            </Popconfirm>
          )}
          <Button
            type="default"
            size="small"
            icon={<PrinterOutlined />}
            onClick={() => handlePrint(record)}
          >
            Print
          </Button>
          <Button
            type="default"
            size="small"
            icon={<DownloadOutlined />}
            onClick={() => handleDownloadPDF(record)}
          >
            PDF
          </Button>
        </Space>
      )
    }
  ];

  return (
    <div>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Bills"
              value={stats.totalBills}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Amount Due"
              value={stats.totalAmountDue}
              prefix="$"
              precision={2}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Overdue Bills"
              value={stats.overdueBills}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Overdue Amount"
              value={stats.totalAmountOverdue}
              prefix="$"
              precision={2}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
      </Row>

      <Card
        title="Vendor Bills (Accounts Payable)"
        extra={
          <Space>
            <Button icon={<DownloadOutlined />} onClick={handleExport}>
              Export
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreate}
            >
              Create Vendor Bill
            </Button>
          </Space>
        }
      >
        <Space style={{ marginBottom: 16, width: '100%' }} direction="vertical">
          <Row gutter={8}>
            <Col flex="auto">
              <Input
                placeholder="Search by bill number or vendor name"
                prefix={<SearchOutlined />}
                value={filters.searchTerm}
                onChange={(e) => setFilters({ ...filters, searchTerm: e.target.value })}
                onPressEnter={handleSearch}
                allowClear
              />
            </Col>
            <Col>
              <Select
                placeholder="State"
                style={{ width: 120 }}
                value={filters.state}
                onChange={(val) => setFilters({ ...filters, state: val })}
                allowClear
              >
                <Option value="DRAFT">Draft</Option>
                <Option value="POSTED">Posted</Option>
                <Option value="PAID">Paid</Option>
                <Option value="CANCELLED">Cancelled</Option>
              </Select>
            </Col>
            <Col>
              <Select
                placeholder="Payment State"
                style={{ width: 130 }}
                value={filters.paymentState}
                onChange={(val) => setFilters({ ...filters, paymentState: val })}
                allowClear
              >
                <Option value="NOT_PAID">Not Paid</Option>
                <Option value="PARTIAL">Partial</Option>
                <Option value="PAID">Paid</Option>
              </Select>
            </Col>
            <Col>
              <Button type="primary" onClick={handleSearch}>
                Search
              </Button>
            </Col>
            <Col>
              <Button onClick={handleResetFilters}>
                Reset
              </Button>
            </Col>
          </Row>
        </Space>

        <Table
          columns={columns}
          dataSource={bills}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1400 }}
          pagination={{
            defaultPageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} bills`
          }}
        />
      </Card>

      <VendorBillForm
        visible={formVisible}
        bill={selectedBill}
        onClose={() => setFormVisible(false)}
        onSuccess={handleFormSuccess}
      />

      <Modal
        title={`Vendor Bill - ${selectedBill?.billNo}`}
        open={detailsVisible}
        onCancel={() => setDetailsVisible(false)}
        footer={[
          <Button key="print" icon={<PrinterOutlined />} onClick={() => selectedBill && handlePrint(selectedBill)}>
            Print
          </Button>,
          <Button key="download" icon={<DownloadOutlined />} onClick={() => selectedBill && handleDownloadPDF(selectedBill)}>
            Download PDF
          </Button>,
          <Button key="close" onClick={() => setDetailsVisible(false)}>
            Close
          </Button>
        ]}
        width={900}
      >
        {selectedBill && (
          <div>
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={12}>
                <p><strong>Vendor:</strong> {selectedBill.vendor?.name}</p>
                <p><strong>Vendor Code:</strong> {selectedBill.vendor?.code}</p>
                <p><strong>Bill Date:</strong> {dayjs(selectedBill.billDate).format('YYYY-MM-DD')}</p>
                <p><strong>Due Date:</strong> {selectedBill.dueDate ? dayjs(selectedBill.dueDate).format('YYYY-MM-DD') : '-'}</p>
              </Col>
              <Col span={12}>
                <p><strong>State:</strong> <Tag color={getStateColor(selectedBill.state)}>{selectedBill.state}</Tag></p>
                <p><strong>Payment State:</strong> <Tag color={getPaymentStateColor(selectedBill.paymentState)}>{selectedBill.paymentState.replace('_', ' ')}</Tag></p>
                <p><strong>Total Amount:</strong> ${Number(selectedBill.amountTotal).toFixed(2)}</p>
                <p><strong>Amount Due:</strong> ${Number(selectedBill.amountResidual).toFixed(2)}</p>
              </Col>
            </Row>

            <div style={{ marginTop: 16 }}>
              <strong>Line Items:</strong>
              <Table
                dataSource={selectedBill.items}
                rowKey="id"
                size="small"
                pagination={false}
                style={{ marginTop: 8 }}
                columns={[
                  { title: 'Description', dataIndex: 'description', key: 'description' },
                  { title: 'Quantity', dataIndex: 'quantity', key: 'quantity', align: 'right' },
                  {
                    title: 'Unit Price',
                    dataIndex: 'unitPrice',
                    key: 'unitPrice',
                    align: 'right',
                    render: (val) => `$${Number(val).toFixed(2)}`
                  },
                  {
                    title: 'Discount',
                    dataIndex: 'discount',
                    key: 'discount',
                    align: 'right',
                    render: (val) => `$${Number(val || 0).toFixed(2)}`
                  },
                  {
                    title: 'Subtotal',
                    dataIndex: 'subtotal',
                    key: 'subtotal',
                    align: 'right',
                    render: (val) => `$${Number(val).toFixed(2)}`
                  }
                ]}
              />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default VendorBillList;
